import { Button as DXButton, DataGrid, Form, LoadPanel, Popup } from 'devextreme-react';
import CustomStore from "devextreme/data/custom_store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { emailLocalizationService } from "../../services/email-localization-service";
import DataGridWrapper from "@/core/wrappers/data-grid-wrapper";
import { t } from "i18next";
import { RemoveMessage } from "@/core/helper/validation-rule-helper";
import PermissionWrapper from "@/core/wrappers/permission-wrapper";
import { ELEMENT_ID } from '@/core/constants/common/element-constants';
import { IEmailLocalization } from '@/core/interfaces/admin/usestate-interfaces';
import { languageService } from '@/core/services/language-service';
import HtmlEditor, { HtmlEditorTypes, Item, MediaResizing, Toolbar } from 'devextreme-react/cjs/html-editor';
import { ButtonItem, GroupItem, Label, RequiredRule, SimpleItem } from 'devextreme-react/cjs/form';
import { ButtonType } from 'devextreme/common';
import { createCustomStore } from '@/core/wrappers/custom-store-wrapper';


const isActiveOptions = {
    elementAttr: {
        [ELEMENT_ID]: "isActive",
    },
    value: false,
};

const EmailLocalization = (props: any) => {
    const [mainStore, setMainStore] = useState<CustomStore>();
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [valueContent, setValueContent] = useState('');
    const emailNotificationId = props.emailNotificationId;
    const dataGridRef = useRef<DataGrid>(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [closedPopup, setClosedPopup] = useState(false);
    const [emailLocalization, setEmailLocalization] = useState<IEmailLocalization>({
        id: '',
        emailNotificationId: emailNotificationId,
        subject: '',
        htmlContent: '',
        languageId: '',
        isActive: false
    });
    const formRef: any = useRef<Form>();

    const languages = useRef(() => createCustomStore({
        key: 'id',
        load: languageService.getLanguages,
        byKey: (key, loadOptions) => {
            if (key) {
                loadOptions.filter = ["Id", "=", key];
            }
        },
    })).current;

    const languageEditorOptions = useMemo(() => ({
        inputAttr: { [ELEMENT_ID]: 'language' },
        dataSource: languages,
        displayExpr: 'description',
        valueExpr: 'id',
    }), []);


    const goToAdd = async () => {
        setPopupVisible(true);
        setEmailLocalization({
            id: '',
            emailNotificationId: emailNotificationId,
            subject: '',
            htmlContent: '',
            languageId: '',
            isActive: false
        })
        setValueContent('');
    }

    const onPopupHiding = useCallback(() => {
        setPopupVisible(false);
    }, [setPopupVisible]);

    const getEmailLocalizationById = useCallback((emailLocalizationId: string) => {
        if (emailLocalizationId) {
            (async function () {
                const emailLocalization = await emailLocalizationService.get(emailLocalizationId)
                const data = emailLocalization.data;
                setEmailLocalization({
                    id: data.id,
                    emailNotificationId: data.emailNotificationId,
                    subject: data.subject,
                    htmlContent: data.htmlContent,
                    languageId: data.languageId,
                    isActive: data.isActive
                })
                setPopupVisible(true);
                setValueContent(data.htmlContent);
            })();
        }
    }, []);
    useEffect(() => {
        setMainStore(createCustomStore({
            key: ['id'],
            load: emailLocalizationService.getListAsync,
            byKey: (key, loadOptions) => {
                if (key) {
                    loadOptions.filter = ["id", "=", key];
                }
            },
            onLoading(loadOptions) {
                if (emailNotificationId) {
                    loadOptions.filter = [["emailNotificationId", "=", emailNotificationId]]
                }
            },
            remove: (key) => {
                return emailLocalizationService.deleteAsync(`${key.id}`);
            },
            useDefaultSearch: true
        }));
    }, [closedPopup]);


    const valueChanged = useCallback((e: HtmlEditorTypes.ValueChangedEvent) => {
        setValueContent((prevValue) => {
            formRef?.current?.instance?.updateData("htmlContent", e.value === "" ? null : e.value);
            if (prevValue === e.value) return prevValue;
            return e.value;
        });
    }, []);


    const [isMultiline, setIsMultiline] = useState(true);
    const subjectEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'subject' },
    };
    const emailNotificationIdEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'emailNotificationId' },
        visible: false
    };

    const registerButtonOptions = {
        elementAttr: { [ELEMENT_ID]: "registerButton" },
        text: t('save'),
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '120px'
    };

    const formSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        setLoadPanelVisible(true);
        const emailLocalizationForm = formRef.current.props.formData;
        console.log(emailLocalization);
        if (!emailLocalization.id) {
            emailLocalizationService.createAsync(emailLocalizationForm);
        }
        else {
            emailLocalizationService.updateAsync(emailLocalization.id, emailLocalizationForm);
        }
        setLoadPanelVisible(false);
        setPopupVisible(false);
        setClosedPopup(true);

    }


    return (
        <>
            <Popup
                visible={popupVisible}
                onHiding={onPopupHiding}
                showTitle={true}
                width={1200}
                height={750}
                position={{
                    of: window,
                    at: 'center',
                    my: 'center'
                }}
                title={t("email_localization")}
            >
                <LoadPanel
                    shadingColor="rgba(0,0,0,0.4)"
                    visible={loadPanelVisible}
                    showIndicator={true}
                    shading={true}
                    showPane={true}
                    hideOnOutsideClick={false}
                    message={t("saving_please_wait")}

                />
                <form id="content-layout" onSubmit={formSubmit} style={{ height: "100%" }} >
                    <Form
                        ref={formRef}
                        formData={emailLocalization}
                    >
                        <GroupItem key={"email-localization"}>
                            <SimpleItem
                                key={"emailNotificationId"}
                                dataField={"emailNotificationId"}
                                editorType={"dxTextBox"}
                                editorOptions={emailNotificationIdEditorOptions}
                                visible={false}
                            >
                            </SimpleItem>
                            <SimpleItem
                                key={"subject"}
                                dataField={"subject"}
                                editorType={"dxTextBox"}
                                editorOptions={subjectEditorOptions}
                            >
                                <Label text={t("subject")} />
                                {<RequiredRule message={t("Subject is required")} />}
                            </SimpleItem>
                        </GroupItem>
                        <GroupItem>
                            <Label text={t("html_content")} />
                            <HtmlEditor height="350px" value={valueContent} onValueChanged={valueChanged} valueType='html'>
                                <MediaResizing enabled={true} />
                                <Toolbar multiline={isMultiline}>
                                    <Item name="undo" />
                                    <Item name="redo" />
                                    <Item name="separator" />
                                    <Item name="redo" />
                                    <Item name="separator" />
                                    <Item name="separator" />
                                    <Item name="bold" />
                                    <Item name="italic" />
                                    <Item name="strike" />
                                    <Item name="underline" />
                                    <Item name="separator" />
                                    <Item name="alignLeft" />
                                    <Item name="alignCenter" />
                                    <Item name="alignRight" />
                                    <Item name="alignJustify" />
                                    <Item name="separator" />
                                    <Item name="orderedList" />
                                    <Item name="bulletList" />
                                    <Item name="separator" />
                                    <Item name="separator" />
                                    <Item name="color" />
                                    <Item name="background" />
                                    <Item name="separator" />
                                    <Item name="link" />
                                    <Item name="image" />
                                    <Item name="separator" />
                                    <Item name="clear" />
                                    <Item name="codeBlock" />
                                    <Item name="blockquote" />
                                    <Item name="separator" />
                                    <Item name="insertTable" />
                                    <Item name="deleteTable" />
                                    <Item name="insertRowAbove" />
                                    <Item name="insertRowBelow" />
                                    <Item name="deleteRow" />
                                    <Item name="insertColumnLeft" />
                                    <Item name="insertColumnRight" />
                                    <Item name="deleteColumn" />
                                </Toolbar>
                            </HtmlEditor>
                        </GroupItem>
                        <GroupItem>
                            <SimpleItem
                                key={"languageId"}
                                dataField={"languageId"}
                                editorType={"dxSelectBox"}
                                editorOptions={languageEditorOptions}
                            >
                                <Label text={t("language")} />
                                {<RequiredRule message={t("Language is required")} />}
                            </SimpleItem>
                        </GroupItem>
                        <GroupItem>
                        <SimpleItem
                                key={"isActive"}
                                dataField={"isActive"}
                                editorType={"dxSwitch"}
                                editorOptions={isActiveOptions}
                            >
                                <Label text={t("active")} />
                            </SimpleItem>
                        </GroupItem>
                        <GroupItem cssClass={'buttons-group-right'}>
                            <ButtonItem key={"registerBtn"} buttonOptions={registerButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup >
            <div className='row mb-4 justify-content-end mt-5'>
                <PermissionWrapper permission='Admin.Email.EmailLocalization' action='Create'>
                    <div className='col-auto'>
                        <DXButton
                            text={t("new_record")}
                            type='default'
                            icon='add'
                            onClick={goToAdd}
                        />
                    </div>
                </PermissionWrapper >
            </div>
            <DataGridWrapper
                props={{ id: 'email-localization-list', ref: dataGridRef, wordWrapEnabled: true }}
                permissonCode='Admin.Email.EmailLocalization'
                datasource={mainStore}
                editingConfig={{ allowUpdating: true, allowDeleting: true, confirmDeleteMessage: RemoveMessage(t('email_localization')) }}
                pagerVisible={true}
                headerFilterVisible={true}
                columns={[
                    {
                        dataField: 'subject',
                        caption: t('subject'),
                        filtering: false,
                        allowSearch: false,
                    },
                    {
                        dataField: 'isActive',
                        caption: t('active'),
                        filtering: false,
                        allowSearch: false,
                        dataType: 'boolean',
                        calculateCellValue: (rowData: any) => {
                            return rowData.isActive || false;
                        },
                    },
                ]}
                buttonConfig={[
                    {
                        name: 'edit',
                        icon: 'edit',
                        onClick: function (e: any) {
                            getEmailLocalizationById(e.row?.data.id);
                        },
                    },
                    {
                        name: 'delete',
                        icon: 'trash'
                    }
                ]}
            />
        </>
    );
}
export default EmailLocalization;